import React, { FC, useRef, useState, useEffect } from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
    Button,
    FormControl,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    TextField,
    CircularProgress
} from "@material-ui/core";

import { useStyles, useStylesAddress } from './style';
import CountryStateDropdown from "../../UI/CountryStateDropdown";
import { CompanyData, CompanyDataErrorProps, ErrorProps } from "../../../entities/ManageBranding";
import {
    CITY,
    CITY_MANDATORY,
    COMPANY_NAME,
    COMPANY_NAME_MANDATORY,
    COUNTRY,
    COUNTRY_MANDATORY,
    MAX_FILE_SIZE,
    objError,
    PHONE_NUMBER,
    PHONE_NUMBER_INVALID,
    PHONE_NUMBER_MANDATORY,
    POSTAL_CODE_FORMAT,
    STATE, STATE_MANDATORY,
    STREET_ADDRESS_1,
    STREET_ADDRESS_1_MANDATORY,
    ZIP_CODE,
    ZIP_CODE_INVALID,
    ZIP_CODE_MANDATORY
} from './manageBranding.consts';
import { COMPANY_LIST } from "../../../utils/api.config.consts";
import ManageBrandingService from "./ManageBrandingService";
import ReactNotification, { DANGER, DATA_SAVED, ERROR, INVALID_IMG_SIZE, SAVE_ERROR, SAVE_IMG_ERROR, SUCCESS } from "../../../utils/ReactNotification.config";
import { api, base_url } from "../../../utils/env";
import upload_logo from "../../../assets/images/img_upload_logo.png"

import "../../css/ManageBranding.css";

interface ManageBrandingProps {
    companyID: string | null;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    data: CompanyData;
    masterId: string | null;
    selectCompanyID: (CompanyIDValue: string) => void;
}

const ManageBranding: FC<ManageBrandingProps> = ({ companyID, setLoading, data, masterId, selectCompanyID }: ManageBrandingProps) => {
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<any>(undefined);
    const [file, setFile] = useState<File | null>(null);
    const [companyData, setCompanyData] = useState<CompanyData>({} as CompanyData);
    const [countryStateLoader, setCountryStateLoader] = useState<boolean>(false);
    const [errors, setErrors] = useState<CompanyDataErrorProps>(objError);
    const [imgLoader, setImgLoader] = useState<boolean>(false);
    const classes = useStyles();
    const addrClasses = useStylesAddress();

    useEffect(() => {
        getCountryStateList();
    }, [])

    useEffect(() => {
        if (data && data.id && companyID) {
            getImage();
        }
    }, [data]);

    const dataURLtoFile = (dataUrl: string, fileType: string) => {
        const arr: string[] = dataUrl.split(',');
        const bstr: string = atob(arr[1]);
        let n: number = bstr.length;
        const u8arr: Uint8Array = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], "default", { type: fileType });
    }

    const getImage = async () => {
        setImgLoader(true);
        await axios
            .get(`${base_url}${api}/companies/download?masterId=${masterId}&uuid=${data.logoBucketId}&companyId=${companyID}`)
            .then((response: AxiosResponse<any>) => {
                setSelectedFile(
                    response.data.base64Data
                );
                if (response.data.base64Data && response.data.fileType) {
                    setFile(
                        dataURLtoFile(response.data.base64Data, response.data.fileType)
                    );
                }
                setImgLoader(false);
            })
            .catch((error: AxiosError<any>) => {
                setImgLoader(false);
                setFile(null);
            })
    };

    const getCountryStateList = () => {
        axios.get(`${base_url}${api}/country`)
            .then((response: AxiosResponse<any>) => {
                ManageBrandingService.setCountryState(response.data?.countries);
                setCountryStateLoader(true);
            })
    }

    const uploadImg = () => {
        uploadInputRef.current?.click()
    }

    const changeHandlerFileUpload = (event: React.FormEvent<HTMLInputElement>) => {
        if (
            event?.currentTarget?.files != null &&
            event?.currentTarget?.files[0]?.name !== undefined &&
            (
                (
                    event?.currentTarget?.files[0]?.type === "image/jpeg" ||
                    event?.currentTarget?.files[0]?.type === "image/png" ||
                    event?.currentTarget?.files[0]?.type === "image/svg" ||
                    event?.currentTarget?.files[0]?.type === "image/bmp" ||
                    event?.currentTarget?.files[0]?.type === "image/svg+xml"
                ) && event?.currentTarget?.files[0]?.size <= MAX_FILE_SIZE
            )
        ) {
            const fileObject = event.currentTarget.files[0];
            const file = URL.createObjectURL(fileObject);
            setFile(fileObject);
            setSelectedFile(file);
        }
        else if (
            event?.currentTarget?.files != null &&
            event?.currentTarget?.files[0]?.name !== undefined &&
            (
                (
                    event?.currentTarget?.files[0]?.type === "image/jpeg" ||
                    event?.currentTarget?.files[0]?.type === "image/png" ||
                    event?.currentTarget?.files[0]?.type === "image/svg" ||
                    event?.currentTarget?.files[0]?.type === "image/bmp" ||
                    event?.currentTarget?.files[0]?.type === "image/svg+xml"
                ) && event?.currentTarget?.files[0]?.size > MAX_FILE_SIZE
            )
        ) {
            ReactNotification.showNotification(ERROR, INVALID_IMG_SIZE, DANGER);
            event.preventDefault();
        }
        else if (event?.currentTarget?.files?.length === 0) {
            event.preventDefault();
        }
        else {
            ReactNotification.showNotification(ERROR, SAVE_IMG_ERROR, DANGER);
            event.preventDefault();
        }

    }

    const updateBillingAddress = (addressField: string, fieldValue: string) => {
        let value = fieldValue;
        if (addressField === PHONE_NUMBER) {
            value = value.replace("(", "");
            value = value.replace(")", "");
            value = value.replace("-", "");
            value = value.replace(" ", "");
            if (fieldValue.length > 10)
                value = value.substring(0, 10);
        }
        if (addressField === COUNTRY) {
            setCompanyData({
                ...companyData,
                [COUNTRY]: value,
                [STATE]: "default"
            });
        }
        else {
            setCompanyData({
                ...companyData,
                [addressField]: value
            });
        }

    };

    const formatPhoneNumber = (phoneNumberString: string) => {
        let cleaned = (`''${phoneNumberString}`).replace(/\D/g, '');
        let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        cleaned = cleaned.replace("(", "");
        cleaned = cleaned.replace(")", "");
        cleaned = cleaned.replace("-", "");
        cleaned = cleaned.replace(" ", "");
        return cleaned;
    }

    const setError = (property = "", status = false, message = "") => {
        setErrors({
            ...errors,
            [property]: {
                status: status,
                message: message,
            },
        });
    };

    const validation = (
        property: string,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | React.ChangeEvent<{ value: any }>,
        countryOrStateValue?: string
    ) => {
        const changeEvent = event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
        changeEvent.preventDefault();
        changeEvent.stopPropagation();
        const value = changeEvent.currentTarget.value && changeEvent.currentTarget.value.trim();

        if (property === COMPANY_NAME) {
            if (!value) setError(property, true, COMPANY_NAME_MANDATORY);
            else setError(property);
        }

        if (property === STREET_ADDRESS_1) {
            if (!value) setError(property, true, STREET_ADDRESS_1_MANDATORY);
            else setError(property);
        }

        if (property === COUNTRY) {
            if (!countryOrStateValue) setError(property, true, COUNTRY_MANDATORY);
            else if (countryOrStateValue === "default") setError(property, true, COUNTRY_MANDATORY);
            else setError(property);
        }

        if (property === STATE) {
            if (!countryOrStateValue) setError(property, true, STATE_MANDATORY);
            else if (countryOrStateValue === "default") setError(property, true, STATE_MANDATORY);
            else setError(property);
        }

        if (property === CITY) {
            if (!value) setError(property, true, CITY_MANDATORY);
            else setError(property);
        }

        if (property === ZIP_CODE) {
            if (!value) setError(property, true, ZIP_CODE_MANDATORY);
            else if (!POSTAL_CODE_FORMAT.test(value)) setError(property, true, ZIP_CODE_INVALID);
            else if (value.length < 5 || value.length > 6) setError(property, true, ZIP_CODE_INVALID);
            else setError(property);
        }

        if (property === PHONE_NUMBER) {

            let phoneNumberValue = value.replace("(", "");
            phoneNumberValue = phoneNumberValue.replace(")", "");
            phoneNumberValue = phoneNumberValue.replace("-", "");
            phoneNumberValue = phoneNumberValue.replace(" ", "");

            if (!value) setError(property, true, PHONE_NUMBER_MANDATORY);
            else if (phoneNumberValue.length < 10) {
                setError(property, true, PHONE_NUMBER_INVALID);
            }
            else setError(property);
        }
    };

    const phoneNumberLength = (phoneNumber: any) => {
        let digitStr = String(phoneNumber).match(/\d+/g);
        if (digitStr) {
            return digitStr.join("").length;
        }
        return 0;
    }

    const setErrorsOnConfirm = () => {
        let companyNameError: ErrorProps;
        let streetAddressError: ErrorProps;
        let countryError: ErrorProps;
        let stateError: ErrorProps;
        let cityError: ErrorProps;
        let zipCodeError: ErrorProps;
        let phoneNumberError: ErrorProps;
        let valid: boolean = false;

        if (!companyData.companyName) {
            companyNameError = {
                status: true,
                message: COMPANY_NAME_MANDATORY,
            }
            valid = true;
        }
        else {
            companyNameError = {
                status: false,
                message: "",
            }
        }

        if (!companyData.streetAddress1) {
            streetAddressError = {
                status: true,
                message: STREET_ADDRESS_1_MANDATORY,
            }
            valid = true;
        }
        else {
            streetAddressError = {
                status: false,
                message: "",
            }
        }

        if (!companyData.country) {
            countryError = {
                status: true,
                message: COUNTRY_MANDATORY,
            }
            valid = true;
        }
        else if (companyData.country === "default") {
            countryError = {
                status: true,
                message: COUNTRY_MANDATORY,
            }
            valid = true;
        }
        else {
            countryError = {
                status: false,
                message: "",
            }
        }

        if (!companyData.state) {
            stateError = {
                status: true,
                message: STATE_MANDATORY,
            }
            valid = true;
        }
        else if (companyData.state === "default") {
            stateError = {
                status: true,
                message: STATE_MANDATORY,
            }
            valid = true;
        }
        else {
            stateError = {
                status: false,
                message: "",
            }
        }


        if (!companyData.city) {
            cityError = {
                status: true,
                message: CITY_MANDATORY,
            }
            valid = true;
        }
        else {
            cityError = {
                status: false,
                message: "",
            }
        }

        if (!companyData.zipCode) {
            zipCodeError = {
                status: true,
                message: ZIP_CODE_MANDATORY,
            }
            valid = true;
        }
        else if (!POSTAL_CODE_FORMAT.test(companyData.zipCode)) {
            zipCodeError = {
                status: true,
                message: ZIP_CODE_INVALID,
            }
            valid = true;
        }
        else if (companyData.zipCode.length < 5 || companyData.zipCode.length > 6) {
            zipCodeError = {
                status: true,
                message: ZIP_CODE_INVALID,
            }
            valid = true;
        }
        else {
            zipCodeError = {
                status: false,
                message: "",
            }
        }

        if (!companyData.phoneNumber) {
            phoneNumberError = {
                status: true,
                message: PHONE_NUMBER_MANDATORY,
            }
            valid = true;
        }
        else if (phoneNumberLength(companyData.phoneNumber) < 10) {
            phoneNumberError = {
                status: true,
                message: PHONE_NUMBER_INVALID,
            }
            valid = true;
        }
        else {
            phoneNumberError = {
                status: false,
                message: "",
            }
        }

        setErrors({
            companyName: companyNameError,
            streetAddress1: streetAddressError,
            country: countryError,
            state: stateError,
            city: cityError,
            zipCode: zipCodeError,
            phoneNumber: phoneNumberError
        });
        return valid;
    }

    const onConfirmValidation = async () => {
        if (!setErrorsOnConfirm())
            saveDetails()
    };

    const saveDetails = () => {
        setLoading(true);
        const postData = async () => {
            const postData = {
                "companyId": companyID?.trim(),
                "masterId": masterId,
                "companyName": companyData?.companyName?.trim(),
                "streetAddress1": companyData?.streetAddress1?.trim(),
                "streetAddress2": companyData?.streetAddress2?.trim(),
                "city": companyData?.city?.trim(),
                "country": companyData?.country,
                "state": companyData?.state,
                "zipCode": companyData?.zipCode?.trim(),
                "phoneNumber": companyData?.phoneNumber,
            };
            if (file) {
                const formData = new FormData();
                formData.append('file', file as File);
                formData.append('companyRequest', JSON.stringify(postData));
                await axios
                    .post(`${base_url}${api}/${COMPANY_LIST}`,
                        formData
                    )
                    .then(() => {
                        ReactNotification.showNotification("Company Info", DATA_SAVED, SUCCESS);
                        setLoading(false);
                    })
                    .catch(() => {
                        setLoading(false);
                        ReactNotification.showNotification(ERROR, SAVE_ERROR, DANGER);
                    });
            }
            else {
                setLoading(false);
                ReactNotification.showNotification(ERROR, SAVE_IMG_ERROR, DANGER);
            }
        }
        postData();
    };

    useEffect(() => {
        setCompanyData(data);
        setErrors(objError);
        setSelectedFile(undefined);
    }, [data])


    const checkDisable = (): boolean => {
        return (companyID && companyID.trim() !== "" && companyID !== "default") ? false : true;
    }

    const formatString = (fieldValue: string | null) => {
        return (fieldValue && fieldValue.trim() !== "") ? fieldValue : "";
    }

    const resetFields = () => {
        selectCompanyID("");
        setSelectedFile(undefined);
        setFile(null);
    }

    return (
        <section className="manage-branding-container">
            <div className="box-shadow  custom-card-manage-branding">
                <Grid container direction="row" md={12} xs={12}>
                    <Grid container direction="row">
                        <span className="title-label-branding">Company Information</span>
                    </Grid>
                    <Grid container direction="row" md={12} xs={12} className="grid-margin-brand">
                        <Grid container direction="row" md={12} xs={12}>
                            <div className="required-star">
                                <FormControl className={classes.formControl}>
                                    <TextField
                                        id="company-name-manage"
                                        label="Company Name"
                                        name="company-name"
                                        variant="outlined"
                                        size="small"
                                        inputProps={{ className: classes.input }}
                                        error={errors.companyName.status}
                                        helperText={errors.companyName.message}
                                        disabled={checkDisable()}
                                        value={formatString(companyData?.companyName)}
                                        onChange={
                                            (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                updateBillingAddress(COMPANY_NAME, event.currentTarget.value);
                                                validation(COMPANY_NAME, event);
                                            }
                                        }
                                        onBlur={(event: any) => validation(COMPANY_NAME, event)}
                                    />
                                </FormControl>
                                <div className={`required-default`}>*</div>
                            </div>
                        </Grid>
                        <Grid container direction="row" md={8} xs={12}>
                            <Grid container direction="row" md={12} xs={12}>
                                <div className="required-star">
                                    <FormControl className={classes.formControl}>
                                        <TextField
                                            id="street-address-manage"
                                            label="Street Address"
                                            name="street-address"
                                            variant="outlined"
                                            size="small"
                                            inputProps={{ className: classes.input }}
                                            error={errors.streetAddress1.status}
                                            helperText={errors.streetAddress1.message}
                                            disabled={checkDisable()}
                                            value={formatString(companyData?.streetAddress1)}
                                            onChange={
                                                (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                    updateBillingAddress(STREET_ADDRESS_1, event.currentTarget.value);
                                                    validation(STREET_ADDRESS_1, event);
                                                }
                                            }
                                            onBlur={(event: any) => validation(STREET_ADDRESS_1, event)}
                                        />
                                    </FormControl>
                                    <div className={`required-default`}>*</div>
                                </div>
                            </Grid>
                            <Grid container direction="row" md={12} xs={12}>
                                <div className="non-required">
                                    <FormControl className={classes.formControl}>
                                        <TextField
                                            id="apt-id-manage"
                                            label="Apt, Suite, Unit, Building, Floor etc."
                                            name="apt"
                                            variant="outlined"
                                            size="small"
                                            inputProps={{ className: classes.input }}
                                            disabled={checkDisable()}
                                            value={formatString(companyData?.streetAddress2)}
                                            onChange={
                                                (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                    updateBillingAddress("streetAddress2", event.currentTarget.value)
                                                }
                                            }
                                        />
                                    </FormControl>
                                </div>
                            </Grid>
                            <Grid container direction="row" md={12} xs={12}>

                                <Grid container direction="row" md={4} xs={12}>
                                    <div className="required-star">
                                        <FormControl error={errors.country.status} className={addrClasses.formControl} variant="outlined" size="small">
                                            <InputLabel id="Country">Country</InputLabel>
                                            <CountryStateDropdown disabledState={checkDisable()} loader={countryStateLoader} type={COUNTRY} updateChange={updateBillingAddress} value={companyData?.country ? companyData?.country : "default"} validate={validation} />
                                            {errors.country.status ?
                                                <FormHelperText variant="outlined" error={true}> {errors.country.message}</FormHelperText>
                                                : ""}
                                        </FormControl>
                                        <div className={`required-default`}>*</div>
                                    </div>
                                </Grid>
                                <Grid container direction="row" md={4} xs={12}>
                                    <div className="required-star">
                                        <FormControl error={errors.state.status} className={addrClasses.formControl} variant="outlined" size="small">
                                            <InputLabel id="State">State/Province</InputLabel>
                                            <CountryStateDropdown disabledState={checkDisable()} loader={countryStateLoader} type="state" country={companyData?.country ? companyData?.country : "default"} updateChange={updateBillingAddress} value={companyData?.state ? companyData.state : "default"} validate={validation} />
                                            {errors.state.status ?
                                                <FormHelperText variant="outlined" error={true}>{errors.state.message}</FormHelperText>
                                                : ""}
                                        </FormControl>
                                        <div className={`required-default`}>*</div>
                                    </div>
                                </Grid>
                                <Grid container direction="row" md={4} xs={12}>
                                    <div className="required-star">
                                        <FormControl className={addrClasses.formControl}>
                                            <TextField
                                                id="city-manage"
                                                label="City"
                                                name="city"
                                                variant="outlined"
                                                size="small"
                                                inputProps={{ className: classes.input }}
                                                error={errors.city.status}
                                                helperText={errors.city.message}
                                                disabled={checkDisable()}
                                                value={formatString(companyData?.city)}
                                                onChange={
                                                    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                        updateBillingAddress(CITY, event.currentTarget.value);
                                                        validation(CITY, event);
                                                    }
                                                }
                                                onBlur={(event: any) => validation(CITY, event)}
                                            />
                                        </FormControl>
                                        <div className={`required-default`}>*</div>
                                    </div>
                                </Grid>
                            </Grid>
                            <Grid container direction="row" md={12} xs={12}>
                                <Grid container direction="row" md={4} xs={12}>
                                    <div className="required-star">
                                        <FormControl className={addrClasses.formControl}>
                                            <TextField
                                                id="postal-code-manage"
                                                label="Postal Code"
                                                name="postalCode"
                                                variant="outlined"
                                                size="small"
                                                inputProps={{ className: classes.input }}
                                                error={errors.zipCode.status}
                                                helperText={errors.zipCode.message}
                                                disabled={checkDisable()}
                                                value={formatString(companyData?.zipCode)}
                                                onChange={
                                                    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                        updateBillingAddress(ZIP_CODE, event.currentTarget.value)
                                                        validation(ZIP_CODE, event);
                                                    }
                                                }
                                                onBlur={(event: any) => validation(ZIP_CODE, event)}
                                            />
                                        </FormControl>

                                        <div className={`required-default`}>*</div>

                                    </div>
                                </Grid>
                                <Grid container direction="row" md={8} xs={12}>
                                    <div className="required-star">
                                        <FormControl className={classes.formControl}>
                                            <TextField
                                                id="phone-number-manage"
                                                label="Phone Number"
                                                name="phone-number"
                                                variant="outlined"
                                                size="small"
                                                error={errors.phoneNumber.status}
                                                helperText={errors.phoneNumber.message}
                                                disabled={checkDisable()}
                                                inputProps={{ pattern: "[0-9]", className: classes.input }}
                                                value={companyData?.phoneNumber ? formatPhoneNumber(companyData?.phoneNumber) : ""}
                                                onChange={
                                                    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                        updateBillingAddress(PHONE_NUMBER, event.currentTarget.value);
                                                        validation(PHONE_NUMBER, event);
                                                    }
                                                }
                                                onBlur={(event: any) =>
                                                    validation(PHONE_NUMBER, event)}
                                            />
                                        </FormControl>
                                        <div className={`required-default`}>*</div>

                                    </div>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container direction="row" md={4} xs={12}>
                            <Grid container direction="row" className=" img-placeholder-center">
                                <FormControl className={" img-placeholder-center " + classes.formControl}>
                                    {imgLoader ?
                                        <div className="upload-container">
                                            <div className="img-placeholder display-flex">
                                                <CircularProgress className={classes.loaderDiv} color="inherit" />
                                            </div>
                                        </div>
                                        :
                                        <div className="upload-container">
                                            <div
                                                onClick={uploadImg}
                                                className={checkDisable() ? "img-disabled-placeholder"
                                                    : ("img-placeholder " + ((selectedFile !== undefined) ? " display-none " : " display-flex "))} >
                                                <input type="file"
                                                    ref={uploadInputRef}
                                                    disabled={checkDisable()}
                                                    accept="image/jpeg, image/png, image/svg, image/bmp, image/svg+xml"
                                                    onChange={changeHandlerFileUpload}
                                                    className="display-none" />
                                                <img src={upload_logo} alt="" className="img-selected-icon" />
                                                <span className="title-img-upload">Upload Company Logo</span>
                                                <span className="title-img-type">.jpeg, .png, .svg, .bmp file </span>
                                            </div>
                                            <span onClick={uploadImg} className={"img-placeholder " + ((selectedFile !== undefined) ? " display-flex " : " display-none ")}>
                                                <img src={selectedFile} alt="" className="img-selected" />
                                            </span>
                                        </div>

                                    }
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container direction="row" md={12} xs={12} className="btn-container">
                    <Button
                        variant="contained"
                        disabled={checkDisable()}
                        color="primary"
                        className="custom-btn cancel-btn-margin"
                        onClick={resetFields}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={checkDisable()}
                        color="primary"
                        className="custom-btn"
                        onClick={onConfirmValidation}
                    >
                        Save
                    </Button>
                </Grid>
            </div>
        </section>
    );
};

export default ManageBranding;
